import { brand } from '@brand';

import { createTheme, ThemeOptions } from '@mui/material/styles';

import { brandShades } from './brandPalette';

declare module '@mui/material/styles' {
    interface Palette {
        accent: Palette['primary'];
        surface: string;
        cyber: {
            electric: string;
            neon: string;
            matrix: string;
            deep: string;
        };
    }

    interface PaletteOptions {
        accent?: PaletteOptions['primary'];
        surface?: string;
        cyber?: {
            electric: string;
            neon: string;
            matrix: string;
            deep: string;
        };
    }

    interface TypeBackground {
        light: string;
        lighter: string;
    }

    interface TypeText {
        contrast: string;
        placeholder: string;
    }
}

export type ThemeMode = 'light' | 'dark';

export const getTheme = (mode: ThemeMode) => {
    const isLight = mode === 'light';

    // This is the page a recipient opens, often somebody with no account who has never heard of us,
    // so on a dedicated instance it carries the client's colour as well as their logo. Without a
    // palette — every environment of ours — nothing here changes.
    const b = brand.palette?.primary ? brandShades(brand.palette.primary, mode) : null;

    const themeOptions: ThemeOptions = {
        palette: {
            mode,
            primary: {
                main: b?.main ?? (isLight ? '#0052CC' : '#4A90E2'),
                light: b?.light ?? (isLight ? '#2684FF' : '#64B5F6'),
                dark: b?.dark ?? (isLight ? '#003D99' : '#1976D2'),
                contrastText: b?.contrastText ?? '#FFFFFF',
            },
            secondary: {
                main: isLight ? '#6366F1' : '#818CF8',
                light: isLight ? '#818CF8' : '#A5B4FC',
                dark: isLight ? '#4F46E5' : '#6366F1',
                contrastText: '#FFFFFF',
            },
            accent: {
                main: isLight ? '#06B6D4' : '#22D3EE',
                light: isLight ? '#22D3EE' : '#67E8F9',
                dark: isLight ? '#0891B2' : '#06B6D4',
                contrastText: isLight ? '#FFFFFF' : '#0F172A',
            },
            background: {
                default: isLight ? '#F8FAFC' : '#0F172A',
                paper: isLight ? '#FFFFFF' : '#1E293B',
                light: isLight ? '#F1F5F9' : '#334155',
                lighter: isLight ? '#E2E8F0' : '#475569',
            },
            text: {
                primary: isLight ? '#0F172A' : '#c0c7ca',
                secondary: isLight ? '#475569' : '#add8ee',
                contrast: isLight ? '#FFFFFF' : '#1E293B',
                disabled: isLight ? '#94A3B8' : '#64748B',
                placeholder: isLight ? 'rgba(71, 85, 105, 0.6)' : 'rgba(203, 213, 225, 0.6)',
            },
            error: {
                main: isLight ? '#a82121' : '#EF4444',
                light: isLight ? '#F87171' : '#FCA5A5',
                dark: isLight ? '#8a1a1a' : '#770606',
                contrastText: '#FFFFFF',
            },
            warning: {
                main: isLight ? '#F59E0B' : '#FBBF24',
                light: isLight ? '#FCD34D' : '#FDE68A',
                dark: isLight ? '#D97706' : '#F59E0B',
                contrastText: isLight ? '#FFFFFF' : '#0F172A',
            },
            info: {
                main: isLight ? '#0EA5E9' : '#38BDF8',
                light: isLight ? '#38BDF8' : '#7DD3FC',
                dark: isLight ? '#0284C7' : '#0EA5E9',
                contrastText: '#FFFFFF',
            },
            success: {
                main: isLight ? '#10B981' : '#34D399',
                light: isLight ? '#34D399' : '#6EE7B7',
                dark: isLight ? '#059669' : '#10B981',
                contrastText: '#FFFFFF',
            },
            divider: isLight ? '#E2E8F0' : '#334155',
            surface: isLight ? '#FFFFFF' : '#1E293B',
            cyber: {
                electric: '#A855F7',
                neon: '#06B6D4',
                matrix: '#10B981',
                deep: '#6366F1',
            },
        },
        typography: {
            fontFamily: [
                'Inter',
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
            ].join(','),
            h1: { fontWeight: 700, letterSpacing: '-0.02em' },
            h2: { fontWeight: 700, letterSpacing: '-0.01em' },
            h3: { fontWeight: 600, letterSpacing: '-0.01em' },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 600 },
            h6: { fontWeight: 600 },
            button: { fontWeight: 500, letterSpacing: '0.01em' },
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        padding: '10px 20px',
                        transition: 'all 0.2s ease',
                    },
                    contained: {
                        boxShadow: isLight ? '0 1px 3px rgba(0, 0, 0, 0.12)' : '0 1px 3px rgba(0, 0, 0, 0.5)',
                        '&:hover': {
                            boxShadow: isLight
                                ? '0 4px 12px rgba(0, 0, 0, 0.15)'
                                : '0 4px 12px rgba(74, 144, 226, 0.3)',
                            transform: 'translateY(-1px)',
                        },
                    },
                    outlined: {
                        borderWidth: '1.5px',
                        '&:hover': {
                            borderWidth: '1.5px',
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: '4px',
                        backgroundImage: 'none',
                    },
                    elevation1: {
                        boxShadow: isLight ? '0 1px 3px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.5)',
                    },
                    elevation2: {
                        boxShadow: isLight ? '0 2px 8px rgba(0, 0, 0, 0.1)' : '0 2px 8px rgba(0, 0, 0, 0.6)',
                    },
                },
            },
            // The bar spans the window, so it has no corners to round. It is a Paper, which is where
            // the radius above reached it from, and it showed as a notch at each end of the header.
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        borderRadius: 0,
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: '4px',
                        border: isLight ? '1px solid #E2E8F0' : '1px solid #334155',
                        boxShadow: isLight ? '0 2px 8px rgba(0, 0, 0, 0.08)' : '0 2px 8px rgba(0, 0, 0, 0.5)',
                        transition: 'all 0.2s ease',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: isLight ? '#FFFFFF' : '#1E293B',
                            borderRadius: '4px',
                            minHeight: '44px',
                            transition: 'all 0.2s ease',
                        },
                    },
                },
            },
            MuiAlert: {
                styleOverrides: {
                    root: {
                        borderRadius: '4px',
                    },
                    standardSuccess: {
                        backgroundColor: isLight ? '#ECFDF5' : '#064E3B',
                        color: isLight ? '#065F46' : '#6EE7B7',
                    },
                    standardError: {
                        backgroundColor: isLight ? '#FEF2F2' : '#7F1D1D',
                        color: isLight ? '#991B1B' : '#FCA5A5',
                    },
                    standardWarning: {
                        backgroundColor: isLight ? '#FFFBEB' : '#78350F',
                        color: isLight ? '#92400E' : '#FDE68A',
                    },
                    standardInfo: {
                        backgroundColor: isLight ? '#EFF6FF' : '#0C4A6E',
                        color: isLight ? '#1E40AF' : '#7DD3FC',
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            backgroundColor: isLight ? 'rgba(0, 82, 204, 0.08)' : 'rgba(74, 144, 226, 0.12)',
                        },
                    },
                },
            },
        },
        shape: {
            borderRadius: 8,
        },
    };

    return createTheme(themeOptions);
};
